import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Action, Icon, colors } from '@soluto-private/mx-asurion-ui-react';

import { useAnalytics } from '@service-initiation/shared-data-access-analytics';
import { useGoToPrevStep } from '@service-initiation/shared-data-access-flow-manager';
import { LeftMainNavigationButtonContainer as DefaultLeftMainNavigationButtonContainer } from '@service-initiation/shared-ui-page-layout';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { type Route } from '@service-initiation/tech-care-data-access-routing';

export type NavigationButtonsContainerProps = {
  backButtonText?: string;
  continueButtonAnalyticsExtras?: Record<string, unknown>;
  continueButtonText?: string;
  disableContinueButton?: boolean;
  isLoading?: boolean;
  onContinueButtonClick?: () => void;
  pageName: keyof typeof Route;
  showBackButton?: boolean;
};

export const ButtonContainer = styled(DefaultLeftMainNavigationButtonContainer)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;
  background: ${colors.white};
  width: 100%;
`;

export const NavigationButtonsContainer = ({
  backButtonText,
  continueButtonAnalyticsExtras,
  continueButtonText,
  disableContinueButton,
  isLoading = false,
  onContinueButtonClick,
  pageName,
  showBackButton = true,
}: NavigationButtonsContainerProps) => {
  const [, goToPrevStep] = useGoToPrevStep();
  const { dispatchClickEvent } = useAnalytics();
  const { t } = useTranslation();

  const backButtonDefaultText = t(
    TranslationEntries.Repairs.StepManagerButtons.Back
  );
  const continueButtonDefaultText = t(
    TranslationEntries.Repairs.StepManagerButtons.Continue
  );

  const handleBackButtonClick = () => {
    dispatchClickEvent('backNavigationButton', {
      Cta: 'Back Button',
      Page: pageName,
    });
    goToPrevStep();
  };

  const handleContinueButtonClick = () => {
    dispatchClickEvent('continueNavigationButton', {
      Cta: continueButtonText || continueButtonDefaultText,
      ...(continueButtonAnalyticsExtras ? continueButtonAnalyticsExtras : {}),
      Page: pageName,
    });
    onContinueButtonClick && onContinueButtonClick();
  };

  return showBackButton || onContinueButtonClick ? (
    <ButtonContainer>
      {showBackButton ? (
        <Action
          color={'white'}
          data-testid="nav-buttons-back"
          onClick={handleBackButtonClick}
          size="md"
          variant="flat"
        >
          <Icon src="arrow-left" />
          {backButtonText || backButtonDefaultText}
        </Action>
      ) : (
        <div />
      )}
      {onContinueButtonClick && (
        <Action
          color="primary"
          data-testid="nav-buttons-next"
          disabled={disableContinueButton}
          onClick={handleContinueButtonClick}
          size="md"
          state={isLoading ? 'loading' : 'enabled'}
          type="submit"
        >
          {continueButtonText || continueButtonDefaultText}
        </Action>
      )}
    </ButtonContainer>
  ) : null;
};
